import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import TradeInStepper from '../../components/TradeInStepper.js';
import Pagination from '../../components/Pagination.js';
import noImage from '../../assets/img/brand/noImage.png';
import apiHelper from '../../apiHelper.js';
import { set } from 'date-fns';

// ─── Constants ────────────────────────────────────────────────────────────────
const YELLOW = '#FFEB00';


// ─── Image Reselect Modal ─────────────────────────────────────────────────────
function ImageReselectModal({ skuId, skuImages, spuImages, initialSelected, onCancel, onConfirm }) {

    // Determine which tab + image to show when modal opens
    const resolveInitial = () => {
        if (!initialSelected) {
            const first = skuImages[0] || null;
            return { tab: 'sku', img: first ? { ...first, tab: 'sku' } : null };
        }

        // If previously saved with an explicit tab, try to find it in that tab's list first
        const tryTab = (list, tab) => {
            const found = list.find(s =>
                (initialSelected.id && s.id === initialSelected.id) ||
                (initialSelected.url && s.url === initialSelected.url)
            );
            return found ? { tab, img: { ...found, tab } } : null;
        };

        // Prefer the stored tab hint, then try the other tab
        const hintTab = initialSelected.tab || 'sku';
        const hintList = hintTab === 'sku' ? skuImages : spuImages;
        const otherTab = hintTab === 'sku' ? 'spu' : 'sku';
        const otherList = hintTab === 'sku' ? spuImages : skuImages;

        const inHint  = tryTab(hintList, hintTab);
        if (inHint) return inHint;

        const inOther = tryTab(otherList, otherTab);
        if (inOther) return inOther;

        // Previously selected image has been deleted → fallback to first SKU image
        return { tab: 'sku', img: skuImages[0] ? { ...skuImages[0], tab: 'sku' } : null };
    };

    const { tab: initTab, img: initImg } = resolveInitial();
    const [activeTab, setActiveTab] = useState(initTab);
    const [selected, setSelected]   = useState(initImg);

    const images = activeTab === 'sku' ? skuImages : spuImages;

    const isSameImage = (a, b) => {
        if (!a || !b) return false;
        if (a.id !== null && b.id !== null) return a.id === b.id && a.tab === b.tab;
        return a.url === b.url && a.tab === b.tab;
    };

    const toggle = (img) => {
        const tagged = { ...img, tab: activeTab };
        setSelected(prev => isSameImage(prev, tagged) ? null : tagged);
    };

    const isSelected = (img) => isSameImage(selected, { ...img, tab: activeTab });

    const tabStyle = (tab) => ({
        height: '35px',
        minWidth: 88,
        padding: '10px',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'Montserrat',
        cursor: 'pointer',
        border: 'none',
        borderRadius: 8,
        background: activeTab === tab ? '#FFEB00' : 'transparent',
        // borderBottom: activeTab === tab ? `2px solid #212529` : '2px solid transparent',
        color: activeTab === tab ? '#212529' : '#6C757D',
    });

    return (
        <>
            <div
                className="drawer-overlay"
                onClick={onCancel}
                style={{ zIndex: 1040 }}
            />
            <div style={{
                position: 'fixed',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0px 4px 24px rgba(0,0,0,0.18)',
                zIndex: 1050,
                width: '90%',
                maxWidth: 1130,
                minHeight: '640px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '15px 20px',
                    // borderBottom: '1px solid #E9ECEF',
                    borderRadius: '12px 12px 0 0',
                    background: '#F8F9FA',
                    flexShrink: 0,
                }}>
                    <span style={{ fontWeight: 600, fontSize: 14, fontFamily: 'Montserrat', color: '#212529' }}>
                        Select Listing Item Image<span style={{ color: '#DC3545' }}>*</span>
                    </span>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                    >
                        <X size={18} color="#000" />
                    </button>
                </div>


                {/* Body */}
                <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
                            Showing all images of the SKU and its SPU in ERP. Select one to use as the Listing Item Image.
                        </span>
                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
                            Total: {images.length}
                        </span>
                    </div>
                    {/* Tabs */}
                    <div style={{
                        display: 'flex', flexShrink: 0, borderRadius: '8px', padding: '10px', margin: '10px 0px',
                        background: '#FFF',
                        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)', gap: 8
                    }}>
                        <button style={tabStyle('sku')} onClick={() => setActiveTab('sku')}>SKU</button>
                        <button style={tabStyle('spu')} onClick={() => setActiveTab('spu')}>SPU</button>
                    </div>

                    {images.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#ADB5BD', fontSize: 14, fontFamily: 'Montserrat' }}>
                            No images available.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 0px 20px 0px' }}>
                            {images.map(img => (
                                <div
                                    key={img.id}
                                    onClick={() => toggle(img)}
                                    style={{
                                        border: `1px solid ${isSelected(img) ? YELLOW : '#CED4DA'}`,
                                        borderRadius: 4,
                                        cursor: 'pointer',
                                        aspectRatio: '1 / 1',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#FFFFFF',
                                        boxSizing: 'border-box',
                                        transition: 'border-color 0.15s',
                                        width: '115px',
                                        height: '118px',
                                        padding: '10px',
                                    }}
                                >
                                    <img src={img.url || noImage} alt={`img ${img.id}`} onError={e => { e.target.onerror = null; e.target.src = noImage; }} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    padding: '20px 0px',
                    margin: '0px 20px',
                    borderTop: '1px solid #E9ECEF',
                    flexShrink: 0,
                }}>
                    <button type="button" className="dark-button" onClick={onCancel}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            borderRadius: '8px',
                            border: '1px solid #212529',
                            background: '#FFF',
                            height: '34px',
                            minWidth: '100px',
                            padding: '10px',
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        Cancel
                    </button>
                    <button type="button" className="dark-border" onClick={() => onConfirm(selected)}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            borderRadius: '8px',
                            border: '1px solid #212529',
                            background: '#212529',
                            height: '34px',
                            minWidth: '100px',
                            padding: '10px',
                            color: 'white',
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
}

// ─── Custom Checkbox ──────────────────────────────────────────────────────────
function CustomCheckbox({ checked, onChange, label }) {
    return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14, fontFamily: 'Montserrat', fontWeight: 400, color: '#212529', userSelect: 'none' }}>
            <div
                onClick={onChange}
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    border: '1.5px solid #212529',
                    background: checked ? '#FFEB00' : 'white',
                    boxShadow: checked ? '0 1px 4px 0 rgba(0, 0, 0, 0.10)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    transition: 'background 0.15s, box-shadow 0.15s',
                }}
            >
                {checked && (
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 5.5L4.5 8.5L9.5 2.5" stroke="#212529" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </div>
            {label}
        </label>
    );
}

// ─── Color Swatch Input ───────────────────────────────────────────────────────
function ColorCodeInput({ value, onChange, error }) {
    const isValidHex = /^[0-9A-Fa-f]{0,6}$/.test(value);
    const displayColor = value && value.length === 6 ? `#${value}` : '#E9ECEF';

    return (
        <div>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                border: `1px solid ${error ? '#DC3545' : '#6C757D'}`,
                borderRadius: 6,
                overflow: 'hidden',
                height: 40,
                background: 'white',
                padding: '10px'
            }}>
                {/* Color circle */}
                <div style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: displayColor,
                    border: '1px solid #CED4DA',
                    flexShrink: 0,
                    // marginLeft: 8,
                }} />
                {/* Hash symbol */}
                <span style={{
                    padding: '0 4px 0 6px',
                    color: '#212529',
                    fontSize: 14,
                    fontFamily: 'Montserrat',
                    userSelect: 'none',
                }}>
                    #
                </span>
                {/* Hex input */}
                <input
                    type="text"
                    value={value}
                    onChange={e => {
                        const v = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);
                        onChange(v);
                    }}
                    maxLength={6}
                    placeholder="FFFFFF"
                    style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        background: 'transparent',
                        minWidth: 0,
                        color: '#212529'

                        // paddingRight: 8,
                    }}
                />
            </div>
            {error && (
                <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>
            )}
        </div>
    );
}

// ─── Clearable Text Input ─────────────────────────────────────────────────────
function ClearableInput({ value, onChange, onBlur, placeholder, error, disabled }) {
    return (
        <div>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onBlur={e => onBlur && onBlur(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        height: 40,
                        border: `1px solid ${error ? '#DC3545' : '#212529'}`,
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        padding: '10px',
                        paddingRight: 28,
                        background: disabled ? '#F8F9FA' : 'white',
                        outline: 'none',
                        color: '#212529',
                        boxSizing: 'border-box',
                    }}
                />
                {value && !disabled && (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        style={{
                            position: 'absolute',
                            right: 6,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: '#212529',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M9 3L3 9M3 3L9 9" stroke="#212529" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </button>
                )}
            </div>
            {error && (
                <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

export default function AddEditTradeInListingStep2() {
    const history = useHistory();
    const location = useLocation();

    const listing = location.state?.listing || {};
    const isEditMode = Boolean(location.state?.isEdit);
    const restoredItems = location.state?.listingItems;
    const restoredPricingMap = location.state?.pricingMap || null;
    const restoredOriginalConditions = location.state?.originalConditions || null;

    const [items, setItems] = useState([]);
    const [skusLoading, setSkusLoading] = useState(true);
    const [rowErrors, setRowErrors] = useState({});

    // ── Fetch enabled SKUs for the selected SPU ──────────────────────────────
    useEffect(() => {
        // Coming back from Step 3 — restore user-edited items directly
        if (restoredItems && restoredItems.length > 0) {
            setItems(restoredItems);
            if (restoredOriginalConditions) {
                originalConditionsRef.current = restoredOriginalConditions;
            }
            setSkusLoading(false);
            return;
        }

        const spuId = listing.spu?.id;
        if (!spuId) { setSkusLoading(false); return; }

        async function fetchSkus() {
            try {
                const response = await apiHelper(`inventory/sku/spu/${spuId}/`, 'GET');
                if (!response?.ok) return;

                const result = await response.json();
                const enabled = (Array.isArray(result) ? result : []).filter(sku =>
                    sku.enable_display === true ||
                    sku.enable_display === 'true' ||
                    sku.enable_display === 'Enabled' ||
                    sku.enable_display === 1
                );
                const mapped = enabled.map(sku => ({
                    id: sku.sku_id,
                    skuName: sku.sku_name,
                    image: sku.configuration?.images?.[0]
                        ? { id: sku.configuration.images[0].sku_configuration_image_id ?? null, url: sku.configuration.images[0].sku_image_name || noImage }
                        : null,
                    skuImages: (sku.configuration?.images || []).map(img => ({
                        id: img.sku_configuration_image_id ?? null,
                        url: img.sku_image_name || noImage,
                    })),
                    storage: sku.specifications?.find(s => s.attribute_name?.toLowerCase() === 'storage')?.attribute_value || '',
                    color: sku.specifications?.find(s => ['color', 'colour'].includes(s.attribute_name?.toLowerCase()))?.attribute_value || '',
                    colorCode: 'ffffff',
                    conditionNew: !isEditMode,
                    conditionUsed: !isEditMode,
                    checked: false,
                }));

                // In edit mode, fetch saved items and merge their values back
                const tradeinListingId = listing.tradein_listing_id;
                if (isEditMode && tradeinListingId) {
                    try {
                        const itemsRes = await apiHelper(`sales/trade-in-listings/${tradeinListingId}/items/`, 'GET');
                        if (itemsRes?.ok) {
                            const savedData = await itemsRes.json();
                            const savedItems = Array.isArray(savedData) ? savedData : (savedData?.results || []);
                            // Group saved items by sku_id
                            const savedBySkuId = {};
                            savedItems.forEach(si => {
                                const skuId = si.sku_id;
                                if (!savedBySkuId[skuId]) savedBySkuId[skuId] = [];
                                savedBySkuId[skuId].push(si);
                            });

                            const merged = mapped.map(row => {
                                const saved = savedBySkuId[row.id];
                                if (!saved || saved.length === 0) {
                                    return {
                                        ...row,
                                        // Mark which conditions were previously saved (none for new SKUs)
                                        savedConditions: { new: false, used: false },
                                    };
                                }
                                const first = saved[0];
                                const conditions = saved.map(s => (s.tradein_condition || '').toLowerCase());
                                const imageUrl = first.tradein_image_url || first.user_image_name || null;
                                // If a proper snapshot exists, send id=null so backend uses the
                                // snapshot fallback and never re-resolves the live source.
                                // For legacy items (user_image_name only), keep the live id so
                                // the backend can create a first-time snapshot.
                                const hasSnapshot = Boolean(first.tradein_image_url);
                                return {
                                    ...row,
                                    checked: true,
                                    storage: first.storage || row.storage,
                                    color: first.color || row.color,
                                    colorCode: first.color_code || row.colorCode,
                                    conditionNew: conditions.includes('new'),
                                    conditionUsed: conditions.includes('used'),
                                    image: imageUrl
                                        ? { id: hasSnapshot ? null : (row.image?.id ?? null), url: imageUrl, tab: first.tradein_image_tab || 'sku' }
                                        : row.image,
                                    // Track which conditions were saved — determines new vs old in Step 3
                                    savedConditions: {
                                        new: conditions.includes('new'),
                                        used: conditions.includes('used'),
                                    },
                                };
                            });
                            setItems(merged);
                            // Snapshot original conditions for uncheck detection
                            const snap = {};
                            merged.forEach(r => { snap[r.id] = { conditionNew: r.conditionNew, conditionUsed: r.conditionUsed }; });
                            originalConditionsRef.current = snap;
                            return;
                        }
                    } catch (err) {
                        console.error('Failed to fetch saved listing items:', err);
                    }
                }

                setItems(mapped);
            } catch (err) {
                console.error('Failed to fetch SKUs:', err);
            } finally {
                setSkusLoading(false);
            }
        }
        fetchSkus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [page, setPage] = useState(1);
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(location.state?.isDirty || false);
    const [nextLocation, setNextLocation] = useState(null);
    const [isDiscarding, setIsDiscarding] = useState(false);

    // Uncheck-condition confirmation modal (edit mode only)
    const [uncheckConfirmOpen, setUncheckConfirmOpen] = useState(false);
    const [pendingUncheck, setPendingUncheck] = useState(null); // { rowId, field }

    // Store original conditions snapshot (set once when items load in edit mode)
    const originalConditionsRef = useRef(null); // { [skuId]: { conditionNew, conditionUsed } }

    // Image reselect modal state
    const [reselectModal, setReselectModal] = useState(null); // { rowId, skuId }

    const isDirtyRef = useRef(isDirty);
    const unblockRef = useRef(null);
    const itemsModifiedRef = useRef(false); // true if user changed item selections (invalidates step 3 pricing)
    useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

    // Navigation guard
    useEffect(() => {
        const unblock = history.block((loc) => {
            if (isDirtyRef.current && !isDiscarding) {
                setNextLocation(loc);
                setDiscardModalOpen(true);
                return false;
            }
        });
        unblockRef.current = unblock;
        return () => {
            unblock();
            unblockRef.current = null;
        };
    }, [history, isDiscarding]);

    // ── Before-unload (browser tab/window close) ──
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isDiscarding]);

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
    };

    // ── Helpers ────────────────────────────────────────────────────────────────
    const updateItem = (id, field, value) => {
        setIsDirty(true);
        if (field === 'conditionNew' || field === 'conditionUsed') {
            itemsModifiedRef.current = true;
        }

        setItems(prev =>
            prev.map(r =>
                r.id === id ? { ...r, [field]: value } : r
            )
        );

        setRowErrors(prev => {
            const clone = { ...prev };

            if (clone[id]) {
                clone[id] = {
                    ...clone[id],
                    [field]: ''
                };
            }

            return clone;
        });
    };


    // Intercepts condition checkbox toggle — shows confirmation before unchecking a saved condition
    const handleConditionToggle = (rowId, field) => {
        const currentValue = items.find(r => r.id === rowId)?.[field];
        const isSavedCondition = isEditMode &&
            originalConditionsRef.current?.[rowId]?.[field] === true;

        if (currentValue && isSavedCondition) {
            setPendingUncheck({ rowId, field });
            setUncheckConfirmOpen(true);
        } else {
            updateItem(rowId, field, !currentValue);
        }
    };

    // ── Pagination ─────────────────────────────────────────────────────────────
    const totalPages = Math.ceil(items.length / PAGE_SIZE);
    const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    // ── Validate ───────────────────────────────────────────────────────────────
    const validate = () => {
        let hasError = false;
        const newRowErrors = {};

        // Validate all items: storage + color required, condition required, duplicate check
        const combos = {};
        items.forEach(item => {
            if (!item.storage.trim() || !item.color.trim()) return;
            const key = `${item.storage.trim().toLowerCase()}||${item.color.trim().toLowerCase()}`;
            if (!combos[key]) combos[key] = [];
            combos[key].push(item.id);
        });

        items.forEach(item => {
            const errs = {};
            if (!item.storage.trim()) errs.storage = 'Storage is required';
            if (!item.color.trim()) errs.color = 'Color is required';
            if (!errs.storage && !errs.color) {
                const key = `${item.storage.trim().toLowerCase()}||${item.color.trim().toLowerCase()}`;
                if (combos[key]?.length > 1) errs.duplicate = 'Duplicated';
            }
            if (Object.keys(errs).length > 0) {
                newRowErrors[item.id] = errs;
                hasError = true;
            }
        });

        setRowErrors(newRowErrors);

        if (hasError) {
            showNotification('error', 'Please fix the errors below');
            return false;
        }

        const anyConditionChecked = items.some(item => item.conditionNew || item.conditionUsed);
        if (!anyConditionChecked) {
            showNotification('error', 'Please check at least one Item');
            return false;
        }

        return true;
    };

    // ── Actions ────────────────────────────────────────────────────────────────
    const goToStep3 = () => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        history.push(isEditMode ? '/trade-in-listing/edit-listing/pricing' : '/trade-in-listing/add-listing/pricing', {
            listing,
            listingItems: items,
            isEdit: isEditMode,
            isDirty,
            pricingMap: itemsModifiedRef.current ? null : restoredPricingMap,
            originalConditions: originalConditionsRef.current,
        });
    };

    const handleNext = () => {
        if (!validate()) return;
        goToStep3();
    };

    const handleBack = () => {
        if (unblockRef.current) {
            unblockRef.current();
            unblockRef.current = null;
        }
        const path = isEditMode ? '/trade-in-listing/edit-listing' : '/trade-in-listing/add-listing';
        history.push(path, { listing, listingItems: items, isEdit: isEditMode, isDirty, pricingMap: itemsModifiedRef.current ? null : restoredPricingMap });
    };

    const handleDiscard = () => {
        if (isDirty) setDiscardModalOpen(true);
        else history.push('/trade-in-listing');
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardModalOpen(false);
            setTimeout(() => {
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    history.push(pathname, state);
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    history.push('/trade-in-listing', { successMessage: 'Listing Discarded Successfully' });
                }
            }, 100);
        } else {
            setDiscardModalOpen(false);
            setNextLocation(null);
        }
    };

    // ── Blur validation (all rows) ────────────────────────────────────────────
    const handleBlur = (rowId, field, currentValue) => {
        const messages = { storage: 'Storage is required', color: 'Color is required' };
        const isEmpty = !currentValue?.trim();
        setRowErrors(prev => {
            const rowErrs = { ...(prev[rowId] || {}) };
            if (isEmpty) rowErrs[field] = messages[field];
            else delete rowErrs[field];
            return { ...prev, [rowId]: rowErrs };
        });
    };

    // ── Image reselect ────────────────────────────────────────────────────────
    const openReselect = (rowId, skuId) => setReselectModal({ rowId, skuId });

    const confirmReselect = (img) => {
        if (img) updateItem(reselectModal.rowId, 'image', img);
        setReselectModal(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const pageContainerStyle = {
        // padding: '20px 24px',
        fontFamily: 'Montserrat',
        minHeight: '100%',
        // background: '#F8F9FA',
    };

    const labelStyle = {
        fontSize: 13,
        fontWeight: 600,
        color: '#212529',
        fontFamily: 'Montserrat',
        marginBottom: 4,
        display: 'block',
    };

    // Column widths
    const COL = {
        skuName: '22%',
        image: 100,
        storage: '14%',
        color: '14%',
        colorCode: 130,
        condition: '18%',
    };

    const thStyle = {
        fontSize: 12,
        fontWeight: 700,
        fontFamily: 'Montserrat',
        color: '#212529',
        padding: '10px',
        background: '#E9ECEF',
        borderBottom: '1px solid #DEE2E6',
        whiteSpace: 'nowrap',
    };

    const tdStyle = (hasErr, rowBg = 'white') => ({
        padding: '10px',
        borderBottom: '1px solid #E9ECEF',
        verticalAlign: 'center',
        background: rowBg,
    });

    return (
        <div style={pageContainerStyle}>
            {/* Notification */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    action="create"
                    recordName="Listing"
                    message={notification.message}
                    onClose={() => setNotification(prev => ({ ...prev, show: false }))}
                />
            )}

            {/* Discard Confirmation */}
            {discardModalOpen && (
                <DiscardConfirmationModal
                    isOpen={discardModalOpen}
                    onConfirm={() => handleDiscardConfirm(true)}
                    onCancel={() => handleDiscardConfirm(false)}
                    entityName="Trade-In Listing"
                />
            )}

            {/* Uncheck Condition Confirmation Modal */}
            {uncheckConfirmOpen && (
                <>
                    <div className="drawer-overlay" />
                    <div
                        className="entity-drawer modal-popup-cls"
                        style={{
                            maxWidth: 650,
                            margin: '0 auto',
                            background: 'white',
                            borderRadius: 12,
                            zIndex: 1050,
                            padding: '20px 25px',
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                            height: 'auto',
                            right: 'auto',
                            overflow: 'inherit',
                        }}
                    >
                        {/* Close button */}
                        <div className="d-flex justify-content-end align-items-center pt-0 pe-0">
                            <button
                                type="button"
                                onClick={() => { setUncheckConfirmOpen(false); setPendingUncheck(null); }}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', outline: 'none' }}
                                onFocus={e => e.target.blur()}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18">
                                    <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        {/* Title */}
                        <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: 'normal', fontFamily: 'Montserrat', marginBottom: '10px', paddingTop: '20px' }}>
                            You are going to delist these Listing Items
                        </h3>
                        {/* Body */}
                        <p style={{ paddingBottom: '20px', marginBottom: 0, textAlign: 'left', color: '#6C757D', fontSize: '14px', fontWeight: '400', lineHeight: '20px', fontFamily: 'Montserrat' }}>
                            You have unchecked these Listing Items. Once saved, these Listing Items will be treated as delisted, and announce the downstream platforms.
                        </p>
                        {/* Footer */}
                        <div className="drawer-footer" style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                            <button
                                type="button"
                                onClick={() => { setUncheckConfirmOpen(false); setPendingUncheck(null); }}
                                style={{ display: 'flex', height: '34px', minWidth: '100px', padding: '10px 0px', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '8px', border: '1px solid #212529', background: '#FFF', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', color: '#212529', fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (pendingUncheck) {
                                        updateItem(pendingUncheck.rowId, pendingUncheck.field, false);
                                        setPendingUncheck(null);
                                    }
                                    setUncheckConfirmOpen(false);
                                }}
                                style={{ display: 'flex', height: '34px', minWidth: '100px', padding: '10px 0px', justifyContent: 'center', alignItems: 'center', gap: '10px', borderRadius: '8px', background: '#212529', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)', color: '#FFF', fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Image Reselect Modal */}
            {reselectModal && (
                <ImageReselectModal
                    skuId={reselectModal.skuId}
                    skuImages={(() => { const imgs = items.find(r => r.id === reselectModal.rowId)?.skuImages || []; return imgs.length > 0 ? imgs : [{ id: null, url: noImage }]; })()}
                    spuImages={(() => { const imgs = listing.spu?.images || []; return imgs.length > 0 ? imgs : [{ id: null, url: noImage }]; })()}
                    initialSelected={items.find(r => r.id === reselectModal.rowId)?.image}
                    onCancel={() => setReselectModal(null)}
                    onConfirm={confirmReselect}
                />
            )}

            {/* Stepper */}
            <TradeInStepper currentStep={2} />

            {/* Page card */}
            <div style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

                {/* Page header row */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '20px',
                    borderBottom: '1px solid #E9ECEF',
                    background: 'white',
                    borderRadius: '8px'
                }}>
                    <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529' }}>
                        Listing Items
                    </h5>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button type="button" className="btn dark-border" onClick={handleDiscard}
                            style={{
                                borderRadius: '8px',
                                background: 'none',

                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                color: '#212529'
                            }}>
                            Discard
                        </button>
                        <button type="button" className="btn dark-border" onClick={handleBack}
                            style={{
                                borderRadius: '8px',
                                background: 'none',
                                color: '#212529',
                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex'
                            }}>
                            Back
                        </button>
                        <button type="button" className="btn dark-button" onClick={handleNext}
                            style={{
                                borderRadius: '8px',
                                background: '#212529',

                                /* Shadow/Small */
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                height: '34px',
                                minWidth: '100px',
                                padding: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontWeight: 600,
                                fontSize: '12px',
                                display: 'flex',
                                color: "white"
                            }}>
                            Next
                        </button>
                    </div>
                </div>

                {/* Subtitle */}

                {/* Table */}
                <div style={{ overflowX: 'auto', background: 'white', marginTop: '13px', borderRadius: '12px' }}>
                    <p style={{ padding: '15px 20px', borderRadius: '12px 12px 0 0', background: '#F8F9FA', fontSize: 14, color: '#212529', fontFamily: 'Montserrat', fontWeight: 600 }}>
                        Select SKU as Listing Items and Edit
                    </p>
                    <div style={{ padding: '0px 20px 20px 20px' }}>

                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800, marginTop: '20px' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: COL.skuName }}>SKU Name</th>
                                    <th style={{ ...thStyle, width: COL.image }}>Image</th>
                                    <th style={{ ...thStyle, width: COL.storage }}>Storage</th>
                                    <th style={{ ...thStyle, width: COL.color }}>Color</th>
                                    <th style={{ ...thStyle, width: COL.colorCode }}>Color Code</th>
                                    <th style={{ ...thStyle, width: COL.condition }}>Condition</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedItems.map((row, rowIdx) => {
                                    const errs = rowErrors[row.id] || {};
                                    const hasDuplicate = Boolean(errs.duplicate);
                                    const hasRowErr = Object.keys(errs).length > 0;
                                    const rowBg = rowIdx % 2 === 0 ? 'white' : '#F8F9FA';

                                    return (
                                        <tr key={row.id}>
                                            {/* SKU Name */}
                                            <td style={{ ...tdStyle(hasRowErr, rowBg), verticalAlign: 'center' }}>
                                                <div style={{ fontSize: 12, fontFamily: 'Montserrat', color: '#212529', fontWeight: 400 }}>
                                                    {row.skuName}
                                                </div>

                                            </td>

                                            {/* Image */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.image }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                                    <div style={{
                                                        width: 32,
                                                        height: 32,
                                                        // border: '1px solid #E9ECEF',
                                                        // borderRadius: 6,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: '#FAFAFA',
                                                        overflow: 'hidden',
                                                    }}>
                                                        <img
                                                            src={row.image?.url || noImage}
                                                            alt="sku"
                                                            onError={e => { e.target.onerror = null; e.target.src = noImage; }}
                                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => openReselect(row.id, row.id)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            fontSize: 12,
                                                            fontFamily: 'Montserrat',
                                                            fontWeight: 600,
                                                            color: '#212529',
                                                            background: 'none',
                                                            border: '1px solid #212529',
                                                            borderRadius: 8,
                                                            padding: '10px',
                                                            cursor: 'pointer',
                                                            minWidth: '70px',
                                                            height: '34px',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        Reselect
                                                    </button>
                                                </div>
                                            </td>

                                            {/* Storage */}
                                            <td style={tdStyle(Boolean(errs.storage || hasDuplicate), rowBg)}>
                                                <ClearableInput
                                                    value={row.storage}
                                                    onChange={val => updateItem(row.id, 'storage', val)}
                                                    onBlur={(val) => handleBlur(row.id, 'storage', val)}
                                                    // placeholder="e.g. 128GB"
                                                    error={errs.duplicate ? errs.duplicate : errs.storage}
                                                />
                                            </td>

                                            {/* Color */}
                                            <td style={tdStyle(Boolean(errs.color || hasDuplicate), rowBg)}>
                                                <ClearableInput
                                                    value={row.color}
                                                    onChange={val => updateItem(row.id, 'color', val)}
                                                    onBlur={(val) => handleBlur(row.id, 'color', val)}
                                                    // placeholder="e.g. Black"
                                                    error={errs.duplicate ? errs.duplicate : errs.color}
                                                />
                                            </td>

                                            {/* Color Code */}
                                            <td style={{ ...tdStyle(false, rowBg), width: COL.colorCode, padding: '10px' }}>
                                                <ColorCodeInput
                                                    value={row.colorCode}
                                                    onChange={val => updateItem(row.id, 'colorCode', val)}
                                                    error={errs.colorCode}
                                                />
                                            </td>

                                            {/* Condition */}
                                            <td style={tdStyle(false, rowBg)}>
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                                                    <CustomCheckbox
                                                        checked={row.conditionNew}
                                                        onChange={() => handleConditionToggle(row.id, 'conditionNew')}
                                                        label="New"
                                                    />
                                                    <CustomCheckbox
                                                        checked={row.conditionUsed}
                                                        onChange={() => handleConditionToggle(row.id, 'conditionUsed')}
                                                        label="Used"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {pagedItems.length === 0 && (
                                    <tr>
                                        <td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: '#ADB5BD', fontSize: 14, fontFamily: 'Montserrat' }}>
                                            No SKU items found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end',padding: "0 20px 20px 20px", }}>
                                         {items.length > 0 ? (<span style={{ fontSize: '12px', fontWeight: 400, color: '#212529', fontFamily: 'Montserrat, sans-serif' }}>
                                            {(page - 1) * PAGE_SIZE + 1} - {(page - 1) * PAGE_SIZE + pagedItems.length} of {items.length}
                                        </span>) : ""}
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={setPage}
                                        />
                                    </div>
                </div>


            </div>
        </div>
    );
}
